/**
 * Auto Exchange Hook
 *
 * Shared hook for auto-triggering JWT exchange when the onboarding
 * flow completes. Used by both user and tenant complete steps.
 */

import { useEffect, useRef } from 'react';

interface UseAutoExchangeOptions {
  /** Whether the user is already authenticated */
  isAuthenticated: boolean;
  /** Whether exchange is currently in progress */
  isExchanging: boolean;
  /** Error from previous exchange attempt */
  exchangeError: string | null;
  /** Function to trigger JWT exchange */
  onExchange: () => Promise<boolean>;
}

/**
 * Hook that auto-triggers JWT exchange on mount
 *
 * Only triggers once, even if the component re-renders.
 * Does not trigger if already authenticated, currently exchanging,
 * or if there was a previous error (user must retry manually).
 */
export function useAutoExchange({
  isAuthenticated,
  isExchanging,
  exchangeError,
  onExchange,
}: UseAutoExchangeOptions): void {
  const hasTriggeredExchange = useRef(false);

  useEffect(() => {
    if (
      !hasTriggeredExchange.current &&
      !isAuthenticated &&
      !isExchanging &&
      !exchangeError
    ) {
      hasTriggeredExchange.current = true;
      onExchange();
    }
  }, [isAuthenticated, isExchanging, exchangeError, onExchange]);
}
